import { addressSchema, transformAddressToString } from '@/types/addressType';
import z from 'zod';

export const post = {
  body: z
    .object({
      name: z.string().nonempty({ error: 'Supplier name is required' }),
      email: z.email({ error: 'Enter a valid email' }).nonempty({
        error: 'Supplier email is required',
      }),
      address: addressSchema,
      phone: z.string().nonempty({ error: 'Supplier phone is required' }),
      notes: z.string().optional(),
    })
    .transform((data) => ({
      ...data,
      address: transformAddressToString(data.address),
    })),
  response: z.object({
    id: z.number(),
    created_at: z.date(),
    updated_at: z.date(),
    name: z.string(),
    email: z.email(),
    phone: z.string(),
    address: z.string(),
    notes: z.string().optional(),
  }),
  params: z.never(),
  query: z.never(),
};
const postSchema = z.object(post);
export type Post = z.infer<typeof postSchema>;

const get = {
  body: z.never(),
  response: z.array(
    z.object({
      id: z.number(),
      created_at: z.date(),
      updated_at: z.date(),
      name: z.string(),
      email: z.email(),
      phone: z.string(),
      address: z.string(),
      notes: z.string().optional(),
      is_active: z.boolean().optional().default(true),
      categories: z.array(z.string()).optional(),
      certifications: z.array(z.string()).optional(),
      lead_time_label: z.string().nullable().optional(),
    }),
  ),
  params: z.never(),
  query: z
    .object({
      q: z.string().optional(), // query by name
    })
    .optional(),
};
const getSchema = z.object(get);
export type Get = z.infer<typeof getSchema>;

export const patch = {
  body: z
    .object({
      name: z.string(),
      email: z.email(),
      address: z.string(),
      phone: z.string(),
      notes: z.string(),
    })
    .partial(),
  response: z.object({
    id: z.number(),
    created_at: z.date(),
    updated_at: z.date(),
    name: z.string(),
    email: z.email(),
    phone: z.string(),
    address: z.string(),
    notes: z.string().optional(),
  }),
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const patchSchema = z.object(patch);
export type Patch = z.infer<typeof patchSchema>;

const put = {
  body: z.array(
    z.object({
      name: z.string().nonempty({ error: 'Supplier name is required' }),
      email: z.email({ error: 'Enter a valid email' }).nonempty({
        error: 'Supplier email is required',
      }),
      address: z.string().nonempty({ error: 'Supplier address is required' }),
      phone: z.string().nonempty({ error: 'Supplier phone is required' }),
      notes: z.string().optional(),
    }),
  ),
  response: z.array(
    z.object({
      id: z.number(),
      created_at: z.date(),
      updated_at: z.date(),
      name: z.string(),
      email: z.email(),
      phone: z.string(),
      address: z.string(),
      notes: z.string().optional(),
    }),
  ),
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const putSchema = z.object(put);
export type Put = z.infer<typeof putSchema>;

const getSupplier = {
  body: z.never(),
  response: z.object({
    id: z.number(),
    created_at: z.date(),
    updated_at: z.date(),
    name: z.string(),
    email: z.email(),
    phone: z.string(),
    address: z.string(),
    notes: z.string().optional(),
  }),
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const getSupplierSchema = z.object(getSupplier);
export type GetSupplier = z.infer<typeof getSupplierSchema>;

export const deleteSupplier = {
  body: z.never(),
  response: z.never(),
  params: z.object({
    id: z.coerce.number(),
  }),
  query: z.never(),
};
const deleteSupplierSchema = z.object(deleteSupplier);
export type DeleteSupplier = z.infer<typeof deleteSupplierSchema>;

const getSupplierDetails = {
  body: z.never(),
  response: z.object({
    supplier: z.object({
      id: z.number(),
      name: z.string(),
      address: z.string(),
      categories: z.string().array(),
      certifications: z.string().array(),
      lead_time_label: z.string().nullable(),
      contact: z.object({
        name: z.string(),
        email: z.email(),
        phone: z.string(),
        role: z.string(),
      }),
      terms: z.object({
        payment_terms: z.string().nullable(),
        incoterms: z.string().nullable(),
        currency: z.string().nullable(),
      }),
    }),
    summary_last_90: z.object({
      orders: z.number(),
      window_days: z.number(),
    }),
    score: z.number().nullable().optional(),
    score_status: z.string().optional(),
    insufficient_data: z.boolean().optional(),
    rating_breakdown: z.object({
      on_time_rate: z.number().nullable(),
      avg_days_late: z.number().nullable().optional(),
      fulfillment_rate: z.number().nullable().optional(),
      recent_on_time_rate: z.number().nullable().optional(),
      recent_fulfillment_rate: z.number().nullable().optional(),
      quality_pass_rate: z.number().nullable(),
      avg_lead_days: z.number().nullable(),
      avg_price_change: z.number().nullable(),
      total_pos: z.number().optional(),
      received_pos: z.number().optional(),
      received_lines: z.number().optional(),
      partially_received_lines: z.number().optional(),
      open_pos: z.number().optional(),
      open_lines: z.number().optional(),
      overdue_open_lines: z.number().optional(),
      missing_received_dates: z.number().optional(),
    }),
    score_components: z.record(z.string(), z.any()).optional(),
    score_formula: z.any().optional(),
    capacity_backlog: z.object({
      open_lines: z.number(),
      avg_queue_days: z.number(),
      status: z.string(),
    }),
    last_10_outcomes: z
      .object({
        project_po_number: z.string().nullable(),
        po_number: z.string(),
        part_number: z.string(),
        needed_by: z.date(), // '2026-03-27T04:00:00Z'
        promised_date: z.date(), // '2026-03-27T04:00:00Z'
        received_at: z.string().nullable(),
        days_delta: z.string().nullable(),
        heads_up: z.string().nullable(),
      })
      .array(),
    typical_lead_time: z
      .object({
        item: z.string(),
        lead: z.string(),
      })
      .array(),
  }),
  params: z.object({
    supplier_id: z.coerce.number(),
  }),
  query: z.never(),
};
const getSupplierDetailsSchema = z.object(getSupplierDetails);
export type GetSupplierDetails = z.infer<typeof getSupplierDetailsSchema>;

const scoreCard = {
  body: z.never(),
  response: z.object({
    supplier_id: z.number(),
    total_pos: z.number(),
    open_pos: z.number(),
    qc_pass_rate: z.number().nullable(),
  }),
  params: z.object({
    supplier_id: z.coerce.number(),
  }),
  query: z.never(),
};
const scoreCardSchema = z.object(scoreCard);
export type ScoreCard = z.infer<typeof scoreCardSchema>;

const supplierStats = z.object({
  score: z.number().nullable(),
  score_status: z.string().optional(),
  insufficient_data: z.boolean().optional(),
  open_lines: z.number(),
  overdue_open_lines: z.number(),
  late_lines: z.number(),
  on_time_rate: z.number().nullable(),
  avg_days_late: z.number().nullable().optional(),
  fulfillment_rate: z.number().nullable().optional(),
  recent_on_time_rate: z.number().nullable().optional(),
  recent_fulfillment_rate: z.number().nullable().optional(),
  avg_lead_days: z.number().nullable().optional(),
  qc_pass_rate: z.number().nullable().optional(),
  total_pos: z.number().optional(),
  received_pos: z.number().optional(),
  received_lines: z.number().optional(),
  partially_received_lines: z.number().optional(),
  open_pos: z.number().optional(),
});

export const cards = {
  body: z.never(),
  response: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      address: z.string().nullable().optional(),
      categories: z.array(z.string()).optional().default([]),
      certifications: z.array(z.string()).optional().default([]),
      lead_time_label: z.string().nullable().optional(),
      stats: supplierStats,
    }),
  ),
  params: z.never(),
  query: z.never(),
};

const cardsSchema = z.object(cards);
export type Cards = z.infer<typeof cardsSchema>;

export const details = {
  body: z.never(),
  response: z.object({
    supplier: z.object({
      id: z.number(),
      name: z.string(),
      address: z.string().nullable().optional(),
      categories: z.array(z.string()).optional(),
      certifications: z.array(z.string()).optional(),
      lead_time_label: z.string().nullable().optional(),
      contact: z.object({
        name: z.string().nullable().optional(),
        email: z.string().nullable().optional(),
        phone: z.string().nullable().optional(),
        role: z.string().nullable().optional(),
      }),
      terms: z.object({
        payment_terms: z.string().nullable().optional(),
        incoterms: z.string().nullable().optional(),
        currency: z.string().nullable().optional(),
      }),
    }),
    summary_last_90: z.object({
      orders: z.number(),
      window_days: z.number(),
    }),
    rating_breakdown: z.object({
      on_time_rate: z.number(),
      quality_pass_rate: z.number().nullable().optional(),
      avg_lead_days: z.number().nullable().optional(),
      avg_price_change: z.number().nullable().optional(),
    }),
    capacity_backlog: z.object({
      open_lines: z.number(),
      avg_queue_days: z.number(),
      status: z.enum(['OK', 'AT_RISK', 'CRITICAL']),
    }),
    last_10_outcomes: z.array(
      z.object({
        project_po_number: z.string().nullable(),
        po_number: z.string(),
        part_number: z.string(),
        needed_by: z.date().nullable().optional(),
        promised_date: z.date().nullable().optional(),
        received_at: z.date().nullable().optional(),
        days_delta: z.number().nullable().optional(),
        heads_up: z.boolean().nullable().optional(),
      }),
    ),
    typical_lead_time: z.array(
      z.object({
        item: z.string(),
        lead: z.string(),
      }),
    ),
  }),
  params: z.object({
    supplier_id: z.coerce.number(),
  }),
  query: z.never(),
};
